// ShareRecipeButton.jsx
import React, { useState } from 'react';
import { 
  IconButton, 
  Menu, 
  MenuItem, 
  ListItemIcon, 
  ListItemText,
  Tooltip
} from '@mui/material';
import {
  Share as ShareIcon,
  FileDownload as FileDownloadIcon
} from '@mui/icons-material';

const ShareRecipeButton = ({ calculatedResults, totalWeight }) => {
  const [shareMenuAnchor, setShareMenuAnchor] = useState(null);

  // Share function that formats the recipe data
  const handleShare = async () => {
    const formatIngredients = (ingredients) => {
      return ingredients.map(ing => 
        `• ${ing.name}: ${ing.grams.toFixed(1)}g (${ing.percentage}%)`
      ).join('\n');
    };

    const recipeText = `🍞 My Bread Recipe

📊 Total Dough Weight: ${totalWeight.toFixed(1)}g

🌾 Flour:
${formatIngredients(calculatedResults.flour)}

💧 Hydration:
${formatIngredients(calculatedResults.hydration)}

✨ Extras:
${formatIngredients(calculatedResults.extra)}

Happy baking! 🥖`;

    // Try to use the Web Share API if available
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'My Bread Recipe',
          text: recipeText,
        });
      } catch (error) {
        // User cancelled or error occurred, fall back to clipboard
        if (error.name !== 'AbortError') {
          copyToClipboard(recipeText);
        }
      }
    } else {
      // Fallback to copying to clipboard
      copyToClipboard(recipeText);
    }
  };

  // Fallback function to copy to clipboard
  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      alert('Recipe copied to clipboard!');
    } catch (error) {
      // Final fallback - create a temporary textarea
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand('copy');
        alert('Recipe copied to clipboard!');
      } catch (err) {
        alert('Unable to copy recipe. Please copy manually.');
        console.error('Copy failed:', err);
      }
      document.body.removeChild(textArea);
    }
  };

  // Menu item handlers
  const shareRecipeText = () => {
    setShareMenuAnchor(null);
    handleShare();
  };

  const exportAsJavaScript = () => {
    setShareMenuAnchor(null);
    const jsContent = `// Bread Recipe Export
const recipe = ${JSON.stringify(calculatedResults, null, 2)};

export default recipe;`;
    
    const blob = new Blob([jsContent], { type: 'application/javascript' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'bread-recipe.js';
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportAsJSON = () => {
    setShareMenuAnchor(null);
    const jsonContent = JSON.stringify(calculatedResults, null, 2);
    
    const blob = new Blob([jsonContent], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'bread-recipe.json';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <Tooltip title="Share/Export Recipe">
        <IconButton
          onClick={(e) => setShareMenuAnchor(e.currentTarget)}
          size="large"
          color="primary"
        >
          <ShareIcon />
        </IconButton>
      </Tooltip>
      
      <Menu
        anchorEl={shareMenuAnchor}
        open={Boolean(shareMenuAnchor)}
        onClose={() => setShareMenuAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <MenuItem onClick={shareRecipeText}>
          <ListItemIcon>
            <ShareIcon />
          </ListItemIcon>
          <ListItemText primary="Share as Text" secondary="Copy or share formatted recipe" />
        </MenuItem>
        <MenuItem onClick={exportAsJavaScript}>
          <ListItemIcon>
            <FileDownloadIcon />
          </ListItemIcon>
          <ListItemText primary="Export as JavaScript" secondary="Download .js file for import" />
        </MenuItem>
        <MenuItem onClick={exportAsJSON}>
          <ListItemIcon>
            <FileDownloadIcon />
          </ListItemIcon>
          <ListItemText primary="Export as JSON" secondary="Download .json data file" />
        </MenuItem>
      </Menu>
    </>
  );
};

export default ShareRecipeButton;